import React, { useCallback } from 'react';
import { VisitorBlockContainer, Title, StyledFormField, DeleteGuestIcon, HeaderContainer } from './styles';
import { useIntl } from 'react-intl';
import { FormFieldType } from '@hqo/react-components-library/dist/atoms/form-field';
import { Visitor } from 'store/visitors-emails/types';
import { VisitorFieldErrors, VisitorFieldTouched } from 'components/adding-visitors-block/types';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';

interface VisitorBlockProps {
  value: Visitor;
  onChangeVisitors: (key: number, value: Visitor) => void;
  visitorNumber: number;
  errorConfig?: VisitorFieldErrors;
  touchedConfig?: VisitorFieldTouched;
  onFieldBlur: (fieldName: 'firstName' | 'lastName' | 'email') => void;
  onDeleteGuest: (visitorNumber: number) => void;
}

export const VisitorBlock: React.FC<VisitorBlockProps> = ({
  value,
  onChangeVisitors,
  visitorNumber,
  errorConfig,
  touchedConfig,
  onFieldBlur,
  onDeleteGuest,
}: VisitorBlockProps): JSX.Element => {
  const intl = useIntl();

  const handleChangeVisitors = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeVisitors(visitorNumber, { ...value, [event.target.name]: event.target.value });
    },
    [visitorNumber, onChangeVisitors, value],
  );

  const handleFieldBlur = useCallback(
    (fieldName: 'firstName' | 'lastName' | 'email') => {
      onFieldBlur(fieldName);
    },
    [onFieldBlur],
  );

  const shouldShowError = (fieldName: 'firstName' | 'lastName' | 'email') => {
    return touchedConfig?.[fieldName] && errorConfig?.[fieldName];
  };

  const getErrorMessage = (fieldName: 'firstName' | 'lastName' | 'email') => {
    if (!shouldShowError(fieldName)) return undefined;

    switch (errorConfig?.[fieldName]) {
      case 'error-empty-first-name':
        return intl.formatMessage({ id: 'errorEmptyFirstName' });
      case 'error-empty-last-name':
        return intl.formatMessage({ id: 'errorEmptyLastName' });
      case 'error-empty-email':
        return intl.formatMessage({ id: 'errorEmptyEmail' });
      case 'error-invalid-email':
        return intl.formatMessage({ id: 'errorEmail' });
      default:
        return undefined;
    }
  };

  return (
    <VisitorBlockContainer data-testid={`visitor-block-${visitorNumber}`}>
      <HeaderContainer>
        <Title>{intl.formatMessage({ id: 'guest' }, { guestNumber: visitorNumber })}</Title>
        <DeleteGuestIcon icon={faTrash} onClick={() => onDeleteGuest(visitorNumber)} />
      </HeaderContainer>
      <StyledFormField
        data-testid="guest-first-name-field"
        fieldType={FormFieldType.INPUT}
        label={intl.formatMessage({ id: 'first_name' })}
        onChange={handleChangeVisitors}
        onBlur={() => handleFieldBlur('firstName')}
        placeholder={intl.formatMessage({ id: 'enterGuestFirstName' })}
        value={value?.firstName}
        variant="master"
        name="firstName"
        required
        errorText={getErrorMessage('firstName')}
      />
      <StyledFormField
        data-testid="guest-last-name-field"
        fieldType={FormFieldType.INPUT}
        label={intl.formatMessage({ id: 'last_name' })}
        onChange={handleChangeVisitors}
        onBlur={() => handleFieldBlur('lastName')}
        placeholder={intl.formatMessage({ id: 'enterGuestLastName' })}
        value={value?.lastName}
        variant="master"
        name="lastName"
        required
        errorText={getErrorMessage('lastName')}
      />
      <StyledFormField
        data-testid="guest-email-field"
        fieldType={FormFieldType.INPUT}
        label={intl.formatMessage({ id: 'email' })}
        onChange={handleChangeVisitors}
        onBlur={() => handleFieldBlur('email')}
        placeholder={intl.formatMessage({ id: 'enterGuestEmail' })}
        value={value?.email}
        variant="master"
        name="email"
        required
        errorText={getErrorMessage('email')}
      />
    </VisitorBlockContainer>
  );
};
